import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Modal,
  ModalBody,
  ModalHeader,
} from 'reactstrap';
import { environment } from "../../../environment/environment";
import apiResponseHandler from '../../../utils/apiResponseHandler';
import './ManageWhitelabelModal.css';

const ManageWhitelabelModal = (props) => {
  const { showManageWhitelabelModal, toggle, loadingJob, embeddedSignupJob, employerId } = props;
  const [isKickingOff, setIsKickingOff] = React.useState(false);

  const STATUS = {
    NOT_STARTED: 0,
    IN_PROGRESS: 1,
    COMPLETE: 2,
    FAILED: 3,
    // API returns 'processing' as a string for this status due to backend constraints
    PROCESSING: 'processing'
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case STATUS.NOT_STARTED:
        return <span className="badge badge-secondary">NOT STARTED</span>;
      case STATUS.IN_PROGRESS:
        return <span className="badge badge-info">IN PROGRESS</span>;
      case STATUS.COMPLETE:
        return <span className="badge badge-success">COMPLETE</span>;
      case STATUS.FAILED:
        return <span className="badge badge-danger">FAILED</span>;
      case STATUS.PROCESSING: // Special case only applies to the WABA status
        return <span className="badge badge-info">IN PROGRESS</span>;
      default:
        return <span className="badge badge-secondary">NOT STARTED</span>;
    }
  };

  const handleSetupChatbotKickoff = async () => {
    try {
      setIsKickingOff(true);
      await axios.post(
        `${environment.baseUrl}/whatsapp_provisioning/setup_chatbot_complete_workflow/`,
        { employer_id: employerId }
      );

      Swal.fire({
        title: 'Setup Chatbot Initiated!',
        text: 'The chatbot setup workflow has been started successfully.',
        icon: 'success',
        toast: true,
        position: 'top',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

    } catch (error) {
      apiResponseHandler.handleApiError('Error starting chatbot setup', error);
    } finally {
      setIsKickingOff(false);
    }
  };

  const getActionButton = (status, stepName) => {
    // Check if this is the "Setup Chatbot" step and it's not started
    if (stepName === 'Setup Chatbot' && status === STATUS.NOT_STARTED) {
      return (
        <button
          type="button"
          disabled={isKickingOff}
          className="btn btn-primary btn-sm"
          onClick={handleSetupChatbotKickoff}
        >
          Kick off
        </button>
      );
    }

    // For other steps with status Not Started, show disabled button
    if (status === STATUS.NOT_STARTED) {
      return (
        <button
          disabled
          type="button"
          className="btn btn-outline-secondary btn-sm"
        >
          Kick off
        </button>
      );
    }

    return null;
  };

  const renderJobTable = (signupJob) => {
    const phases = signupJob?.onboarding_progress?.phases || [];

    if (phases.length === 0) {
      return (
        <div className="text-center">
          <p>No whitelabel job found.</p>
        </div>
      );
    }

    const renderTableRows = () => {
      const rows = [];

      // Add WhatsApp Business Account Status row
      rows.push(
        <tr key={signupJob.id} className="phase-row">
          <td>WhatsApp Business Account Status</td>
          <td>{getStatusBadge(signupJob.status)}</td>
          <td>{getActionButton(1, 'WhatsApp Business Account Status')}</td>
        </tr>
      );

      // Add phases and their steps
      phases.forEach((phase, index) => {
        // Add phase row
        rows.push(
          <tr key={`phase-${phase.order_number}`} className="phase-row">
            <td>{phase.name}</td>
            <td>{getStatusBadge(phase.status)}</td>
            <td>{getActionButton(phase.status, phase.name)}</td>
          </tr>
        );

        // Add step rows for this phase (only if phase is not complete)
        if (phase.steps && phase.steps.length > 0 && phase.status !== 2) {
          phase.steps.forEach((step) => {
            let statusText = 'Not Started';
            if (step.status === 2) {
              statusText = 'Complete';
            } else if (step.status === 1) {
              statusText = 'In Progress';
            }

            rows.push(
              <tr key={`step-${phase.order_number}-${step.order_number}`} className="step-row">
                <td className="step-name">
                  {step.name}: {statusText}
                  {step.status === 2 && step.status_message && (
                    <div className="step-message">{step.status_message}</div>
                  )}
                </td>
                <td aria-label="Status column for sub-step" />
                <td aria-label="Action column for sub-step" />
              </tr>
            );
          });
        }

        // Add separator after each phase (except the last one)
        if (index < phases.length - 1) {
          rows.push(
            <tr key={`separator-${phase.order_number}`} className="separator-row">
              <td colSpan="3" aria-label="Section separator"><hr className="section-separator" /></td>
            </tr>
          );
        }
      });

      return rows;
    };

    return (
      <div>
        <h5 className="mb-3 onboarding-title">Onboarding status</h5>
        <table className="table table-borderless">
          <thead>
            <tr>
              <th scope="col" className="text-muted">STEP</th>
              <th scope="col" className="text-muted">STATUS</th>
              <th scope="col" className="text-muted">ACTION</th>
            </tr>
          </thead>
          <tbody>
            {renderTableRows()}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Modal isOpen={showManageWhitelabelModal} toggle={toggle} size="lg" className="manage-whitelabel-modal">
      <ModalHeader toggle={toggle} className="modal-header-custom">
        <h4 className="modal-title">Manage Whitelabel</h4>
      </ModalHeader>
      <ModalBody>
        {loadingJob ? (
          <div className="loading-spinner">Loading...</div>
        ) : (
          renderJobTable(embeddedSignupJob)
        )}
      </ModalBody>
    </Modal>
  );
};

ManageWhitelabelModal.propTypes = {
  showManageWhitelabelModal: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  loadingJob: PropTypes.bool.isRequired,
  employerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  embeddedSignupJob: PropTypes.shape({
    id: PropTypes.number,
    status: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onboarding_progress: PropTypes.shape({
      phases: PropTypes.arrayOf(PropTypes.shape({
        order_number: PropTypes.number,
        name: PropTypes.string,
        status: PropTypes.number,
        steps: PropTypes.array
      }))
    })
  })
};

ManageWhitelabelModal.defaultProps = {
  embeddedSignupJob: {}
};

export default ManageWhitelabelModal;
